<template>
  <div class="graph">
    <div class="card">
      <div :class="['graph-container', { empty: emptyData }]">
        <line-chart
          ref="lineChart"
          :key="$route.name"
          :chart-data="chartData"
          :options="chartOptions"
        />

        <div v-show="emptyData" class="empty-text">
          {{ emptyGraphText }}
        </div>

        <full-logo v-show="!emptyData" />
      </div>
    </div>

    <align
      v-if="!hideControls"
      class="options"
      small-gap
      flex
      direction="flex-start"
    >
      <align small-gap>
        <span>{{ dateRangeText }}</span>

        <span class="date-range-container" v-if="!isPublic">
          <component
            :is="dateRangeComponent"
            v-model="dateRange"
          />
        </span>
      </align>

      <align small-gap>
        <pill-dropdown
          v-model="graphZoom"
          :options="zoomOptions"
          align="top-right"
          label="Custom"
        />

        <pill-dropdown
          v-if="!isPublic"
          v-model="graphScale"
          :options="scaleOptions"
          align="top-right"
        />
      </align>
    </align>
  </div>
</template>

<script>
import moment from 'moment';

import { getDateFromString } from '@cardladder/shared/utils/format.util';

import Align from './Align';
import FullLogo from './FullLogo';
import LineChart from './LineChart';
import PillDropdown from './PillDropdown';

import {
  GRAPH_ZOOM_OPTIONS,
  GRAPH_SCALE_OPTIONS,
} from '../constants';

export default {
  components: {
    Align,
    FullLogo,
    LineChart,
    PillDropdown,
  },

  props: {
    zoom: [Number, Object],
    scale: String,
    labels: Array,
    datasets: Array,
    isPublic: Boolean,
    hideControls: Boolean,
    monthLabels: Boolean,
    yAxisType: {
      type: String,
      default: 'currency',
    },
    emptyGraphText: {
      type: String,
      default: 'No sales data to display',
    },
    hideLegend: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      scaleOptions: GRAPH_SCALE_OPTIONS,
      darkMode: false,
    };
  },

  mounted() {
    setTimeout(() => {
      if (document?.body?.classList?.contains('dark')) {
        this.darkMode = true;
      }
    });
  },

  computed: {
    zoomOptions() {
      return GRAPH_ZOOM_OPTIONS.map((option) => ({
        ...option,
        marker: this.isPublic && !option.isPublic ? 'PRO' : null,
      }));
    },

    dateRangeComponent() {
      if (!this.isPublic) {
        return () => import('./GraphDateRange');
      }

      return null;
    },

    dateRange: {
      get() {
        if (typeof this.zoom === 'object') {
          return this.zoom;
        }

        return {
          start: moment().subtract(this.zoom, 'days').toDate(),
          end: new Date(),
        };
      },

      set(value) {
        this.graphZoom = value;
      },
    },

    timeScale() {
      let daysBetween = this.zoom;

      if (this.dateLabels && this.dateLabels.length === 1) {
        daysBetween = 0;
      }

      if (this.dateLabels && this.dateLabels.length > 1) {
        daysBetween = Math.abs(moment(this.dateLabels[0]).diff(this.dateLabels[this.dateLabels.length - 1], 'days'));
      }

      if (daysBetween <= 60) {
        return 'day';
      }

      if (daysBetween <= 730) {
        return 'month';
      }

      return 'year';
    },

    graphZoom: {
      get() {
        if (!this.zoom) {
          return null;
        }

        if (typeof this.zoom === 'object' && this.zoom.start && this.zoom.end) {
          return this.zoom;
        }

        const activeZoomOption = this.zoomOptions.find(({ value }) => value === this.zoom);

        if (activeZoomOption) {
          return this.zoom;
        }

        return this.zoomOptions[0].value;
      },

      set(value) {
        const zoomOption = GRAPH_ZOOM_OPTIONS.find(({ value: zoomValue }) => value === zoomValue);

        if (this.isPublic && zoomOption && !zoomOption.isPublic) {
          this.$router.push('/pricing');
        } else {
          this.$emit('update:zoom', value);
        }
      },
    },

    graphScale: {
      get() {
        return this.scale;
      },

      set(value) {
        this.$emit('update:scale', value);
      },
    },

    dateLabels() {
      return this.labels.map((dateString) => getDateFromString(dateString));
    },

    dateRangeText() {
      if (!this.graphZoom) {
        if (this.labels && this.labels.length > 0) {
          return `${this.labels[0]} - ${this.labels[this.labels.length - 1]}`;
        }

        return 'All-time';
      }

      const startDate = this.graphZoom.start ? moment(this.graphZoom.start) : moment().subtract(this.graphZoom, 'days');
      const endDate = this.graphZoom.end ? moment(this.graphZoom.end) : moment();

      return `${startDate.format('MM/DD/YYYY')} - ${endDate.format('MM/DD/YYYY')}`;
    },

    emptyData() {
      return this.labels.length === 0;
    },

    chartOptions() {
      return {
        layout: {
          padding: {
            right: 5,
            ...(this.hideLegend && { bottom: 20 }),
          },
        },
        responsive: true,
        onClick: this.handleClick,
        maintainAspectRatio: false,
        legend: {
          display: !this.hideLegend,
          position: 'bottom',
          labels: {
            fontColor: this.darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            usePointStyle: true,
            boxWidth: 2,
          },
        },
        tooltips: {
          mode: 'index',
          position: 'nearest',
          intersect: false,
          titleFontFamily: 'Lato',
          titleFontSize: 14,
          titleMarginBottom: 10,
          bodyFontFamily: 'Poppins',
          bodyFontSize: 18,
          xPadding: 20,
          yPadding: 20,
          cornerRadius: 7,
          itemSort: (a, b) => ((a.yLabel < b.yLabel) ? 1 : -1),
          callbacks: {
            title: (tooltipItems) => {
              if (!tooltipItems || tooltipItems.length < 1) {
                return '';
              }

              return moment(new Date(tooltipItems[0].xLabel)).format(this.monthLabels ? 'MMM YYYY' : 'MMM D, YYYY');
            },
            label: (tooltipItem, chart) => {
              let displayLabel = ` — ${this.$filters[this.yAxisType](tooltipItem.yLabel, true)}`;

              const isMyPurchase = typeof tooltipItem.datasetIndex === 'number'
                && typeof tooltipItem.index === 'number'
                && chart?.datasets
                && chart.datasets[tooltipItem.datasetIndex]?.pointBorderColor
                && chart.datasets[tooltipItem.datasetIndex].pointBorderColor[tooltipItem.index] === '#FF9957';

              const isPrivatePurchase = typeof tooltipItem.datasetIndex === 'number'
                && typeof tooltipItem.index === 'number'
                && chart?.datasets
                && chart.datasets[tooltipItem.datasetIndex]?.pointBorderColor
                && chart.datasets[tooltipItem.datasetIndex].pointBorderColor[tooltipItem.index] === '#3398DB';

              const isFanaticsTopSale = typeof tooltipItem.datasetIndex === 'number'
                && typeof tooltipItem.index === 'number'
                && chart?.datasets
                && chart.datasets[tooltipItem.datasetIndex]?.pointBorderColor
                && chart.datasets[tooltipItem.datasetIndex].pointBorderColor[tooltipItem.index] === '#FFD64D';

              if (isMyPurchase) {
                displayLabel += ' (My Purchase)';
              } else if (isPrivatePurchase) {
                displayLabel += ' (Private Sale)';
              } else if (isFanaticsTopSale) {
                displayLabel += ' (Fanatics Top %)';
              }

              return displayLabel;
            },
            labelColor(tooltipItem, chart) {
              let backgroundColor = '#34BE8B';

              const hasBackgroundColor = typeof tooltipItem.datasetIndex === 'number'
                && typeof tooltipItem.index === 'number'
                && chart?.config?.data?.datasets
                && chart.config.data.datasets[tooltipItem.datasetIndex]
                && chart.config.data.datasets[tooltipItem.datasetIndex].pointBorderColor;

              if (hasBackgroundColor) {
                backgroundColor = chart.config.data.datasets[tooltipItem.datasetIndex].pointBorderColor[tooltipItem.index];
              }

              if (typeof tooltipItem.datasetIndex === 'number' && chart?.config?.data?.datasets[tooltipItem.datasetIndex].useBorderColorForPointColor) {
                backgroundColor = chart.config.data.datasets[tooltipItem.datasetIndex].borderColor;
              }

              return {
                borderColor: '#fff',
                backgroundColor,
              };
            },
          },
        },
        scales: {
          yAxes: [{
            type: this.graphScale,
            ticks: {
              fontColor: this.darkMode ? 'white' : '#09090b',
              beginAtZero: this.yAxisType !== 'number',
              callback: (value, index, values) => {
                if (values.length < 12 || this.graphScale !== 'logarithmic' || index === 0 || index % 3 === 0) {
                  return this.$filters[this.yAxisType](value, true);
                }

                return null;
              },
            },
          }],
          xAxes: [{
            type: 'time',
            time: {
              unit: this.timeScale,
              round: 'day',
              displayFormats: {
                day: 'MMM D, YYYY',
              },
            },
            ticks: {
              fontColor: this.darkMode ? 'white' : '#09090b',
              minRotation: 40,
            },
          }],
        },
      };
    },

    chartData() {
      return {
        labels: this.dateLabels,
        datasets: this.datasets,
      };
    },
  },

  methods: {
    handleClick(evt) {
      const chart = this.$refs.lineChart.$data._chart;
      const chartIndex = chart.getElementAtEvent(evt);

      if (chartIndex.length !== 0) {
        const datasetIndex = chartIndex[0]._datasetIndex;
        const position = chartIndex[0]._index;
        const info = {
          datasetIndex: datasetIndex,
          valueIndex: position,
          label: chart.tooltip._data.labels[position],
          value: chart.tooltip._data.datasets[datasetIndex].data[position],
        };

        this.$emit('saleClicked', info);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.graph {
  .card {
    padding-bottom: 0;
  }
}

.graph-container {
  position: relative;
  padding-top: $gp * .5;

  &.empty {
    > *:not(.empty-text) {
      opacity: 0.3;
    }
  }

  .full-logo {
    --logo-height: 22px;
    position: absolute;
    right: 5px;
    top: 5px;
    opacity: 0.7;
    pointer-events: none;
  }
}

.empty-text {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20px;
}

.date-range-container {
  padding-top: 5px;
}

.card {
  padding: $gp * .5;
}

.options {
  margin-top: $gp;
}
</style>
